import { useEffect, useMemo, useState } from 'react';
import { getDefaultPromptText, saveAiEmailList } from '../utils';
import {
  GPTType,
  PromptConfirmItemType,
  PromptListType,
  ResultType,
} from '../types';
import useAdvancedSummary from './useAdvancedSummary';
import { DEFAULT_MODAL } from '../constants';

export default function useSaveForm({
  userInfo,
  getDefaultSaveName,
  defaultIsSave = true,
}: {
  userInfo: any;
  getDefaultSaveName: () => string;
  defaultIsSave?: boolean;
}) {
  // TODO: 屏蔽Email getAiSaveEmailList(userInfo)
  const [emailList, setEmailList] = useState<Array<string>>([]);
  const [showEmailMessage, setShowEmailMessage] = useState(false);
  const [aiSaveTitle, setAiSaveTitle] = useState(getDefaultSaveName());
  const [isSave, setIsSave] = useState(defaultIsSave);

  function saveEmailList() {
    if (emailList.length > 0) {
      saveAiEmailList(userInfo, emailList);
    }
  }

  return {
    emailList,
    setShowEmailMessage,
    aiSaveTitle,
    setAiSaveTitle,
    isSave,
    setIsSave,
    showEmailMessage,
    setEmailList,
    saveEmailList,
  };
}

export function useSaveDetailForm({
  userInfo,
  getDefaultSaveName,
  defaultIsSave = true,
  promptValue,
  promptList,
  hasAdvancedSummary,
  loading,
}: {
  userInfo: any;
  getDefaultSaveName: () => string;
  defaultIsSave?: boolean;
  promptValue?: PromptConfirmItemType;
  promptList: PromptListType;
  hasAdvancedSummary?: boolean;
  loading?: boolean;
}) {
  const {
    remainder,
    total,
    setRemainder,
    loading: advancedLimitLoading,
    gptOptions,
    setGptOptions,
  } = useAdvancedSummary({
    defaultValue: promptValue?.gptOptions || 'advanced',
    needRequest: hasAdvancedSummary,
  });
  const {
    emailList,
    setShowEmailMessage,
    aiSaveTitle,
    setAiSaveTitle,
    isSave,
    setIsSave,
    showEmailMessage,
    setEmailList,
    saveEmailList,
  } = useSaveForm({
    userInfo,
    getDefaultSaveName,
    defaultIsSave,
  });

  // 先修改为 page 方便调试
  const [resultType, setResultType] = useState<ResultType>(
    promptValue?.resultType || 'page',
  );
  // promptValue?.modelType || DEFAULT_MODAL
  const [gptType, setGptType] = useState<GPTType>(DEFAULT_MODAL);

  const [realPromptList, promptIsArray] = useMemo(() => {
    const isArray = Array.isArray(promptList);
    if (isArray) return [promptList, isArray];

    return [promptList[gptType], isArray];
  }, [gptType]);

  const [errorText, setErrorText] = useState('');
  const [prompt, setPrompt] = useState('');

  const confirmDisabled = useMemo(() => {
    if (!!errorText) return true;
    if (resultType === 'email' && emailList.length === 0) return true;
    if (gptOptions === 'advanced' && remainder === 0) return true;
    if (isSave && !aiSaveTitle.trim()) {
      return true;
    }
    if (showEmailMessage) {
      return true;
    }
    return false;
  }, [
    errorText,
    gptType,
    resultType,
    emailList,
    gptOptions,
    remainder,
    isSave,
    aiSaveTitle,
    showEmailMessage,
  ]);

  useEffect(() => {
    if (loading !== undefined && loading === false && promptValue) {
      setTimeout(() => {
        setGptOptions(promptValue?.gptOptions || gptOptions);
        setPrompt(promptValue?.prompt || prompt);
        setAiSaveTitle(promptValue?.saveName || aiSaveTitle);
      }, 0);
    }
  }, [loading, promptValue]);

  return {
    resultType,
    emailList,
    setShowEmailMessage,
    aiSaveTitle,
    setAiSaveTitle,
    isSave,
    setIsSave,
    showEmailMessage,
    setEmailList,
    saveEmailList,
    remainder,
    total,
    setRemainder,
    advancedLimitLoading,
    gptOptions,
    setGptOptions,
    setErrorText,
    prompt,
    setPrompt,
    confirmDisabled,
    gptType,
    realPromptList,
    errorText,
    promptIsArray,
    setGptType,
    setResultType,
  };
}
